/**
 * Wave 4.3 — append a library drill to an existing session.
 *
 * Picks the next sort_order so the drill appends to the end of the
 * session's drill list (matches the assumption in SessionRun + the
 * existing useSessionDrills query, both of which order by sort_order
 * ascending).
 *
 * Best-effort + idempotent-ish: if the (session, drill) pair already
 * exists, we don't INSERT a duplicate. We surface that as
 * `{ ok: true, alreadyPresent: true }` so the UI can no-op rather
 * than treating it as an error.
 */
import { supabase } from './supabase';

export interface AddDrillArgs {
  sessionId: string;
  drillId: string;
}

export type AddDrillResult =
  | { ok: true; sessionDrillId: string; sortOrder: number; alreadyPresent: false }
  | { ok: true; alreadyPresent: true }
  | { ok: false; error: string };

export async function addDrillToSession(args: AddDrillArgs): Promise<AddDrillResult> {
  const { sessionId, drillId } = args;
  if (!sessionId || !drillId) {
    return { ok: false, error: 'sessionId and drillId are required' };
  }

  // Bail early if the drill is already on the session — coaches who
  // tap "Add" twice shouldn't end up with the drill duplicated. We
  // check + insert non-atomically; that's fine because the race window
  // is tiny + a duplicate row is a soft failure (the second SessionRun
  // pass would just play the drill twice). A proper unique constraint
  // would force a migration; defer until we see real production
  // duplicates.
  const { data: existing } = await supabase
    .from('session_drills')
    .select('id')
    .eq('session_id', sessionId)
    .eq('drill_id', drillId)
    .limit(1)
    .maybeSingle();
  if (existing?.id) {
    return { ok: true, alreadyPresent: true };
  }

  // Fetch the current max sort_order. Empty session → start at 0.
  const { data: maxRow } = await supabase
    .from('session_drills')
    .select('sort_order')
    .eq('session_id', sessionId)
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();
  const nextOrder =
    typeof maxRow?.sort_order === 'number' ? maxRow.sort_order + 1 : 0;

  const { data, error } = await supabase
    .from('session_drills')
    .insert({
      session_id: sessionId,
      drill_id: drillId,
      sort_order: nextOrder,
    })
    .select('id')
    .single();

  if (error || !data) {
    return { ok: false, error: error?.message ?? 'session_drills insert failed' };
  }
  return {
    ok: true,
    sessionDrillId: String(data.id),
    sortOrder: nextOrder,
    alreadyPresent: false,
  };
}
